import React from 'react';
import { Objective, Review, ReviewCycle } from '../../types/pms';
import { buildReviewTimeline } from '../../services/pmsWorkflow';
import './ReviewTimeline.css';

interface ReviewTimelineProps {
  cycle?: ReviewCycle;
  review?: Review;
  /** Objectives for this employee/cycle — drives the three objective-phase steps. */
  objectives?: Objective[];
}

const formatStamp = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Where-am-I-in-the-process indicator for the whole objective→review journey. Adapts the
 * vertical dot+connector pattern already used by VersionHistory — deliberately reused rather
 * than a new visual language.
 *
 * Purely presentational: the step ordering and done/current rules live in
 * buildReviewTimeline so they can be tested without rendering.
 */
const ReviewTimeline: React.FC<ReviewTimelineProps> = ({ cycle, review, objectives = [] }) => {
  const steps = buildReviewTimeline(cycle, review, objectives);

  return (
    <ol className="pms-review-timeline">
      {steps.map((step) => (
        <li
          key={step.key}
          className={`pms-review-timeline__entry ${step.current ? 'pms-review-timeline__entry--current' : ''}`}
          aria-current={step.current ? 'step' : undefined}
        >
          <span className={`pms-review-timeline__dot ${step.done ? 'pms-review-timeline__dot--done' : ''}`}>
            {step.done ? '✓' : ''}
          </span>
          <span className="pms-review-timeline__label">{step.label}</span>
          {step.timestamp && (
            <span className="pms-review-timeline__stamp">{formatStamp(step.timestamp)}</span>
          )}
        </li>
      ))}
    </ol>
  );
};

export default ReviewTimeline;
